import type { BrownfieldBridgeDeeplink, DeeplinkTypeKeys } from './deeplinkTypes'
import {
  DEEPLINK_TEMPLATE_PARAMS,
  DEEPLINK_URL_CONFIG_KEYS,
  type DeeplinkUrlsConfig,
  resolveDeeplinkUrlsConfig,
} from './deeplinkConfig'

const URL_SEPARATOR_REGEX = /\/+$/

/**
 * Replaces `:param` placeholders in a dynamic route segment with validated
 * values from payload and URL-encodes each value for safe navigation.
 * Example: `:episodeId` + `{ episodeId: 'ep-123' }` -> `ep-123`.
 */
function buildDynamicPathSegment(
  template: string,
  payload: Record<string, unknown> | undefined
): string {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, paramName: string) => {
    const value = payload?.[paramName]
    if (typeof value !== 'string' || value.length === 0) {
      throw new Error(`Missing or invalid deeplink parameter "${paramName}"`)
    }
    return encodeURIComponent(value)
  })
}

/**
 * Returns the ordered dynamic params required by a deeplink type.
 * Example: `episode` -> `['episodeId']`.
 */
function getTemplateParams(type: DeeplinkTypeKeys): readonly string[] {
  return DEEPLINK_TEMPLATE_PARAMS[type as keyof typeof DEEPLINK_TEMPLATE_PARAMS] ?? []
}

/**
 * Builds the final deeplink URL from base URL + optional dynamic params.
 * Examples:
 * - static route: `https://dialogue.co/services`
 * - dynamic route: `https://dialogue.co/episode?channelId=ep-123`
 */
function toUrl(baseUrl: string, type: DeeplinkTypeKeys, payload?: Record<string, unknown>): string {
  const templateParams = getTemplateParams(type)

  if (!templateParams.length) {
    return baseUrl
  }

  const dynamicSegmentTemplate = templateParams.map((paramName) => `:${paramName}`).join('/')
  const dynamicSegment = buildDynamicPathSegment(dynamicSegmentTemplate, payload)
  return `${baseUrl.replace(URL_SEPARATOR_REGEX, '')}${dynamicSegment}`
}

export class DeeplinkRouter {
  constructor(private readonly deeplinkUrlsConfig?: DeeplinkUrlsConfig) {}

  /**
   * Safe routing API: returns resolved URL, or `null` when route resolution fails.
   */
  route(deeplink: BrownfieldBridgeDeeplink): string | null {
    try {
      return this.routeOrThrow(deeplink)
    } catch {
      return null
    }
  }

  /**
   * Strict routing API: resolves deeplink URL and throws on config/param issues.
   */
  routeOrThrow(deeplink: BrownfieldBridgeDeeplink): string {
    const deeplinkUrlsConfig = this.deeplinkUrlsConfig ?? resolveDeeplinkUrlsConfig()

    if (!deeplinkUrlsConfig) {
      throw new Error('Deeplink URLs are not configured')
    }

    const deeplinkConfigKey = DEEPLINK_URL_CONFIG_KEYS[deeplink.type]
    const baseUrl = deeplinkUrlsConfig[deeplinkConfigKey]

    if (!baseUrl || typeof baseUrl !== 'string') {
      throw new Error(`Missing deeplink URL for type "${deeplink.type}"`)
    }

    return toUrl(baseUrl, deeplink.type, 'payload' in deeplink ? deeplink.payload : undefined)
  }
}

export const deeplinkRouter = new DeeplinkRouter()
